import { useMemo, useState } from 'react';
import {
  listDocName,
  scaleIngredientForDisplay,
  type Recipe,
  type UnitSystem,
} from '@awwyeah/shared';
import { ensureStructured } from '../utils/ensureStructured';
import { Check, X } from 'lucide-react';
import { getDocManager } from '../../../sync';
import { useVisibleLists } from '../../lists';
import { addIngredientsToList } from '../listWrite';
import type { ToastAction } from './Toast';

export interface AddedSummary {
  title: string;
  subtitle?: string;
  action: ToastAction;
}

export interface IngredientPickerSheetProps {
  recipe: Recipe;
  userId: string;
  onClose: () => void;
  onAdded: (summary: AddedSummary) => void;
  /** Current detail-page scale factor (1 = unscaled). Ingredients are added to the list
   * pre-scaled — doubling a recipe should double the groceries. */
  scaleFactor?: number;
  /** Current detail-page units toggle (`null` = "as written"). Ingredients are added
   * pre-converted, same non-destructive-to-the-recipe rule as the detail page display. */
  unitSystem?: UnitSystem | null;
}

/**
 * Screens 16 (Recipes): multi-select ingredients → pick a shopping list → confirm. Simpler
 * than the full design mock (single target list, no store-split/merge logic — ROADMAP §2
 * scope for WS-6 is "append items", not the dedupe/merge behaviour lists itself owns) but
 * keeps the two real steps: selection, then destination.
 */
export function IngredientPickerSheet({
  recipe,
  userId,
  onClose,
  onAdded,
  scaleFactor = 1,
  unitSystem = null,
}: IngredientPickerSheetProps): JSX.Element {
  const [step, setStep] = useState<'select' | 'pick-list'>('select');
  const [selected, setSelected] = useState<Set<string>>(
    () => new Set(recipe.ingredients.map((i) => i.id)),
  );

  // Display/write text reflects the active scale + units — the whole point of scaling a
  // recipe is that doubling it doubles the groceries. The stored `Recipe` never changes;
  // this is computed fresh on every render.
  const displayById = useMemo(() => {
    const map = new Map<string, string>();
    for (const ing of recipe.ingredients) {
      map.set(
        ing.id,
        scaleIngredientForDisplay(ensureStructured(ing), scaleFactor, unitSystem ?? null).text,
      );
    }
    return map;
  }, [recipe.ingredients, scaleFactor, unitSystem]);

  // WS-15: this used to read the shared `lists` doc directly, which since the sharing
  // circles landed would silently hide every private list from the destination picker.
  // `useVisibleLists` is the lists module's published seam for exactly this question —
  // one cross-module import of a module's index, not a reach into its internals.
  const lists = useVisibleLists()
    .filter((meta) => !meta.archived)
    .sort((a, b) => a.name.localeCompare(b.name));

  function toggle(id: string): void {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleAll(): void {
    setSelected((prev) =>
      prev.size === recipe.ingredients.length
        ? new Set()
        : new Set(recipe.ingredients.map((i) => i.id)),
    );
  }

  function confirmList(listId: string, listName: string): void {
    const texts = recipe.ingredients
      .filter((i) => selected.has(i.id))
      .map((i) => displayById.get(i.id) ?? i.text);
    if (texts.length === 0) return;

    const handle = getDocManager().acquire(listDocName(listId));
    const { undo } = addIngredientsToList(handle.doc, texts, userId);
    // Keep the socket open long enough to flush the write, then let the manager's normal
    // 15s idle grace (docManager.ts) cover the undo window before the doc actually tears
    // down — undo just replays the same transaction in reverse on the same doc instance.
    let released = false;
    const release = (): void => {
      if (released) return;
      released = true;
      handle.release();
    };
    setTimeout(release, 4000);

    onAdded({
      title: `${texts.length} item${texts.length === 1 ? '' : 's'} → ${listName}`,
      action: {
        label: 'Undo',
        onClick: () => {
          undo();
          release();
        },
      },
    });
    onClose();
  }

  return (
    <div
      className="fixed inset-0 z-40 flex flex-col justify-end"
      data-testid="ingredient-picker-sheet"
    >
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="absolute inset-0 bg-fg opacity-40"
      />
      <div className="relative flex max-h-[85vh] flex-col rounded-t-lg bg-surface pb-6 pt-3">
        <span className="mx-auto mb-3 h-1 w-10 rounded-full bg-line" />

        {step === 'select' ? (
          <>
            <div className="flex items-center justify-between border-b border-line px-4 pb-3">
              <span className="text-lg font-bold text-fg">Pick ingredients</span>
              <button
                type="button"
                onClick={onClose}
                aria-label="Close"
                className="h-9 w-9 text-fg-2"
              >
                <X className="h-5 w-5" aria-hidden="true" />
              </button>
            </div>
            <div className="flex items-center justify-between border-b border-line px-4 py-2.5">
              <button
                type="button"
                onClick={toggleAll}
                data-testid="ingredient-toggle-all"
                className="flex items-center gap-2 text-[15px] font-semibold text-fg"
              >
                <CheckBox checked={selected.size === recipe.ingredients.length} />
                Everything
              </button>
              <span className="text-[13px] text-fg-3">
                {selected.size} of {recipe.ingredients.length} picked
              </span>
            </div>
            <div className="flex-1 overflow-y-auto">
              {recipe.ingredients.map((ing) => (
                <label
                  key={ing.id}
                  data-testid="ingredient-row"
                  className="flex min-h-14 items-center gap-3 border-b border-line px-4"
                >
                  <input
                    type="checkbox"
                    checked={selected.has(ing.id)}
                    onChange={() => toggle(ing.id)}
                    className="sr-only"
                  />
                  <CheckBox checked={selected.has(ing.id)} />
                  <span className="flex-1 text-[15px] text-fg">
                    {displayById.get(ing.id) ?? ing.text}
                  </span>
                </label>
              ))}
            </div>
            <div className="px-4 pt-3">
              <button
                type="button"
                data-testid="ingredient-picker-next"
                disabled={selected.size === 0}
                onClick={() => setStep('pick-list')}
                className="h-[52px] w-full rounded-md bg-inverse text-[16px] font-semibold text-inverse-fg disabled:opacity-50"
              >
                Add {selected.size} item{selected.size === 1 ? '' : 's'} →
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="border-b border-line px-4 pb-3">
              <div className="text-lg font-bold text-fg">Add {selected.size} items to…</div>
              <div className="mt-0.5 text-[13px] text-fg-3">Pick a list</div>
            </div>
            <div className="flex-1 overflow-y-auto" data-testid="list-picker">
              {lists.length === 0 && (
                <p className="px-4 py-6 text-[14px] text-fg-3">
                  No lists yet — create one in Lists first.
                </p>
              )}
              {lists.map((list) => (
                <button
                  key={list.id}
                  type="button"
                  data-testid="list-picker-option"
                  onClick={() => confirmList(list.id, list.name)}
                  className="flex min-h-16 w-full items-center gap-3 border-b border-line px-4 text-left"
                >
                  <span className="text-2xl">{list.emoji ?? '🛒'}</span>
                  <div className="flex-1">
                    <div className="text-[16px] font-semibold text-fg">{list.name}</div>
                    {list.store && <div className="text-xs text-fg-3">{list.store}</div>}
                  </div>
                </button>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function CheckBox({ checked }: { checked: boolean }): JSX.Element {
  return (
    <span
      className={`flex h-7 w-7 shrink-0 items-center justify-center rounded border-2 ${
        checked ? 'border-sync-ok bg-sync-ok' : 'border-line-strong'
      }`}
    >
      {checked && <Check className="h-4 w-4 text-surface" strokeWidth={3} aria-hidden="true" />}
    </span>
  );
}
